import { useEffect, useState } from 'react';
import IResponse from '../../interfaces/response';
import { GridColDef, GridAlignment, DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ICampaign } from '../../interfaces/campaign';
import { formatNumberWithComma, getFarsiNumber } from '../../utils/number';
import { Button, Card, CardContent, Checkbox, IconButton, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import PaymentModal from './PaymentModal.tsx';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoadingButton } from '@mui/lab';
import BasicModal from '../common/BasicModal.tsx';

export default function Cart() {
	const [fetchedData, setFetchedData] = useState<ICampaign[]>([]);
	const [loading, setLoading] = useState(true);
	const [deleteBasket, setDeleteBasket] = useState<ICampaign>();
	const headerClassNames = 'text-xs bg-background-paper';
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
	const [campaigns, setCampaigns] = useState<number[]>([]);
	const [paymentItem, setPaymentItem] = useState<ICampaign[]>([]);

	const handleChange = (item: ICampaign) => {
		setPaymentItem(prevItems => {
			const isItemInArray = prevItems.some(existingItem => existingItem.id === item.id);

			if (isItemInArray) {
				return prevItems.filter(existingItem => existingItem.id !== item.id);
			} else {
				return [...prevItems, item];
			}
		});
	};

	const handleClosePaymentModal = () => {
		setIsPaymentModalOpen(false);
	};

	const handleOpenPaymentModal = () => {
		setIsPaymentModalOpen(true);
	};
	const handleView = (id: number) => () => {
		navigate(`/campaigns/view/${id}?action=show`);
	};
	const handleEdit = (id: number) => () => {
		navigate(`/campaigns/view/${id}?action=edit`);
	};
	const handleCloseDelete = (id: ICampaign) => () => {
		setDeleteBasket(id);
	};
	const handleCloseDeleteDialog = () => {
		setDeleteBasket(undefined);
	};

	const handleReset = () => {
		total = 0;
	};

	const handleDelete = (id: number) => () => {
		axios<IResponse<boolean>>({
			url: `/campaigns/${id}`,
			method: 'DELETE',
		}).then(res => {
			if (res.data.success) {
				FetchDataHandle();
				handleCloseDeleteDialog();
			}
		});
	};

	const columns: GridColDef<ICampaign>[] = [
		{
			field: 'select',
			headerName: t('select'),
			minWidth: 100,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				let item = params.row;
				return <Checkbox value={paymentItem} onChange={() => handleChange(item)} inputProps={{ 'aria-label': 'controlled' }} />;
			},
		},
		{
			field: 'test',
			headerName: t('row'),
			width: 120,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return fetchedData.indexOf(params.row) + 1;
			},
		},
		{
			field: 'id',
			headerName: t('campaign_id'),
			width: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'title',
			headerName: t('campaign_name'),
			minWidth: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'content_type',
			headerName: t('content_type'),
			minWidth: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('created_at'),
			minWidth: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'media',
			headerName: t('platform'),
			minWidth: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'factor_price',
			headerName: t('price'),
			minWidth: 180,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return formatNumberWithComma(params.value);
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerClassName: headerClassNames,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				let data = params.row;
				return (
					<>
						<IconButton onClick={handleEdit(data.id)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton aria-label="eye" onClick={handleView(data.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleCloseDelete(data)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	let FetchDataHandle = () => {
		axios<IResponse<ICampaign[]>>({
			url: `/campaigns/`,
			params: {
				page: 1,
				limit: 100,
				status: 'payment_pend',
			},
		})
			.then(res => {
				if (res.data.success) {
					setFetchedData(res.data.data);
					setCampaigns(res.data.data?.map(i => +i.id));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		setLoading(true);
		FetchDataHandle();
	}, []);

	let total = 0;
	if (paymentItem.length) {
		for (let item of paymentItem) {
			if (item) {
				total += item?.factor_price;
			}
		}
	} else {
		for (let item of fetchedData) {
			if (item.factor_price) {
				total += item?.factor_price;
			}
		}
	}

	return (
		<>
			<Card className={'w-full'}>
				<CardContent>
					<div className="mb-6 flex flex-row items-center justify-between">
						<Typography className="flex items-center text-2xl font-medium">
							{t('cart')}{' '}
							<Typography className="mx-2 text-sm font-medium">
								({getFarsiNumber(fetchedData.length)} {t('value')})
							</Typography>
						</Typography>
						<Button endIcon={<AddIcon />} variant="outlined" component={Link} to="/campaigns/create">
							{t('create_campaign')}
						</Button>
					</div>
					<DataGrid className="-mx-[18px]" loading={loading} rows={fetchedData} columns={columns} localeText={{ noRowsLabel: t('no_rows') }} autoHeight hideFooter disableRowSelectionOnClick disableAutosize disableColumnFilter disableColumnMenu disableColumnResize disableColumnSorting disableColumnSelector />
					<div className="mt-3 flex flex-row items-center justify-between">
						<Button size="large" className="w-44" onClick={handleOpenPaymentModal}>
							{t('checkout')}
						</Button>
						{/* <Typography className="pe-6 text-base">
							{t('amount_payable')}:{formatNumberWithComma(total)}
							{t('tooman')}
						</Typography> */}
					</div>
				</CardContent>
			</Card>

			<PaymentModal rout={'/campaigns/pay-multiple'} showFineDetails={true} amount={total} enableType={true} data={{ campaigns: paymentItem.length ? paymentItem.map(item => item.id) : campaigns }} open={isPaymentModalOpen} onClose={handleClosePaymentModal} handleResultServer={handleReset} />
			<BasicModal
				open={!!deleteBasket}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={loading} onClick={handleDelete(Number(deleteBasket?.id))} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_shopping_basket')}</Typography>
			</BasicModal>
		</>
	);
}
